import { useEffect, useState } from "react";
import { Alert, Card, Col, Row, Table } from "react-bootstrap";
import PageTitle from "@/components/PageTitle";
import DateRangeSelector, {
  buildPresetRange,
} from "@/components/analytics/DateRangeSelector";
import MetricCard from "@/components/analytics/MetricCard";
import { MetricSourceContext } from "@/components/analytics/metricSource";
import FullScreenLoader from "@/components/loaders/screenLoader";
import EmptyState from "@/components/EmptyState";
import { widgetById } from "@/lib/analytics/widgetRegistry";
import { fetchAgenticCheckoutFunnel } from "@/services/api/agentic-checkout.api";
import type {
  AgenticCheckoutFunnelMetrics,
  AgenticCheckoutFunnelResponse,
  AgenticCheckoutMerchantFunnelRow,
} from "@/types/agentic-checkout";
import type { AnalyticsMeta, DateRange } from "@/types/analytics";
import { formatCount, formatRate } from "./format";

// The backend returns null (rendered "—") instead of 0 whenever a rate's
// denominator is empty, so an unmeasured rate never reads as a good one.
const UNMEASURED_REASON = {
  quoteExpiryRate: "Not measured: no clicked intents in window",
  leakRate:
    "Not measured: no eligible denominator (clicked minus expired is 0)",
  attributionLeakRate: "Not measured: no confirmed purchases in window",
} as const;

const RATE_FORMULA = {
  quoteExpiryRate: "Expired / clicked",
  leakRate: "Post-click aborts + failures / (clicked minus expired)",
  attributionLeakRate:
    "Confirmed with no network-reported conversion / confirmed. Networks report with a lag: read over a window ending 2+ days ago.",
} as const;

type RateKey = keyof typeof UNMEASURED_REASON;

const rateHelper = (rate: RateKey, value: number | null) =>
  value === null ? UNMEASURED_REASON[rate] : RATE_FORMULA[rate];

const RateCell = ({ rate, value }: { rate: RateKey; value: number | null }) => (
  <td
    className="text-end"
    title={value === null ? UNMEASURED_REASON[rate] : undefined}
  >
    {formatRate(value)}
  </td>
);

const MERCHANT_COLUMN_COUNT = 16;

// The funnel reads the purchase-intent ledger (kind L), so its tiles read Live (ANL-E04-S09).
const FUNNEL_SOURCE_KIND = widgetById("agentic-checkout.funnel").sourceKind;

const MerchantRow = ({ row }: { row: AgenticCheckoutMerchantFunnelRow }) => (
  <tr>
    <td>{row.affiliateNetwork}</td>
    <td className="pz-mono">{row.merchantId}</td>
    <td className="text-end">{formatCount(row.intents)}</td>
    <td className="text-end">{formatCount(row.clicked)}</td>
    <td className="text-end">{formatCount(row.confirmed)}</td>
    <td className="text-end">{formatCount(row.attributed)}</td>
    <td className="text-end">{formatCount(row.userDeclined)}</td>
    <td className="text-end">{formatCount(row.abortedOther)}</td>
    <td className="text-end">{formatCount(row.failed)}</td>
    <td className="text-end">{formatCount(row.sessionTimeouts)}</td>
    <td className="text-end">{formatCount(row.abandonedExpired)}</td>
    <td className="text-end">{formatCount(row.retries)}</td>
    <td className="text-end">{formatCount(row.stuckPaying)}</td>
    <RateCell rate="quoteExpiryRate" value={row.quoteExpiryRate} />
    <RateCell rate="leakRate" value={row.leakRate} />
    <RateCell rate="attributionLeakRate" value={row.attributionLeakRate} />
  </tr>
);

/** The funnel's headline tiles; they read the ledger (kind L) through the page's source context. */
export const FunnelTotals = ({ totals }: { totals: AgenticCheckoutFunnelMetrics }) => (
  <Row className="g-3 mb-4">
    <Col xs={12} sm={6} xl={3}>
      <MetricCard
        label="Intents"
        value={formatCount(totals.intents)}
        helper="Purchase intents created in window"
      />
    </Col>
    <Col xs={12} sm={6} xl={3}>
      <MetricCard
        label="Clicked"
        value={formatCount(totals.clicked)}
        helper="Affiliate click fired"
      />
    </Col>
    <Col xs={12} sm={6} xl={3}>
      <MetricCard
        label="Confirmed"
        value={formatCount(totals.confirmed)}
        helper="Purchase confirmed"
      />
    </Col>
    <Col xs={12} sm={6} xl={3}>
      <MetricCard
        label="Quote expiry rate"
        value={formatRate(totals.quoteExpiryRate)}
        helper={rateHelper("quoteExpiryRate", totals.quoteExpiryRate)}
      />
    </Col>
    <Col xs={12} sm={6} xl={3}>
      <MetricCard
        label="Leak rate"
        value={formatRate(totals.leakRate)}
        helper={rateHelper("leakRate", totals.leakRate)}
      />
    </Col>
    <Col xs={12} sm={6} xl={3}>
      <MetricCard
        label="Retries"
        value={formatCount(totals.retries)}
        helper="Intents retried from a parent intent"
      />
    </Col>
    <Col xs={12} sm={6} xl={3}>
      <MetricCard
        label="Attribution leak"
        value={formatRate(totals.attributionLeakRate)}
        helper={rateHelper(
          "attributionLeakRate",
          totals.attributionLeakRate
        )}
      />
    </Col>
    <Col xs={12} sm={6} xl={3}>
      <MetricCard
        label="Stuck paying"
        value={formatCount(totals.stuckPaying)}
        helper="Still PAYING past the stuck threshold. Never auto-failed: reconcile against the payment rail."
      />
    </Col>
  </Row>
);

const AgenticCheckoutPage = () => {
  const [range, setRange] = useState<DateRange>(() => buildPresetRange("7d"));
  const [data, setData] = useState<AgenticCheckoutFunnelResponse | null>(null);
  const [meta, setMeta] = useState<AnalyticsMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchAgenticCheckoutFunnel(
          range.start,
          range.end
        );
        if (!mounted) return;
        setData(response.result);
        setMeta(response.meta);
      } catch {
        if (!mounted) return;
        setError("Unable to load the agentic checkout funnel.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [range.start, range.end]);

  const totals = data?.totals;

  return (
    <>
      <PageTitle title="Agentic Checkout" />

      <DateRangeSelector value={range} onChange={setRange} />

      {loading ? <FullScreenLoader /> : null}

      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {!loading && !error && data && totals ? (
        <MetricSourceContext.Provider value={{ kind: FUNNEL_SOURCE_KIND, meta }}>
          <FunnelTotals totals={totals} />

          <Card>
            <Card.Header className="d-flex align-items-center justify-content-between">
              <span>By merchant</span>
              <span className="text-muted small fw-normal pz-mono">
                {data.window.start} – {data.window.end}
              </span>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Network</th>
                    <th>Merchant</th>
                    <th className="text-end">Intents</th>
                    <th className="text-end">Clicked</th>
                    <th className="text-end">Confirmed</th>
                    <th className="text-end">Attributed</th>
                    <th className="text-end">User declined</th>
                    <th className="text-end">Aborted other</th>
                    <th className="text-end">Failed</th>
                    <th className="text-end">Session timeouts</th>
                    <th className="text-end">Expired</th>
                    <th className="text-end">Retries</th>
                    <th className="text-end">Stuck paying</th>
                    <th className="text-end">Expiry rate</th>
                    <th className="text-end">Leak rate</th>
                    <th className="text-end">Attribution leak</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byMerchant.length === 0 ? (
                    <tr>
                      <td colSpan={MERCHANT_COLUMN_COUNT} className="p-0">
                        <EmptyState
                          variant="compact"
                          title="No purchase intents in this window"
                          body="Merchant rows appear as soon as a checkout intent is created in the selected range."
                        />
                      </td>
                    </tr>
                  ) : (
                    data.byMerchant.map((row) => (
                      <MerchantRow
                        key={`${row.affiliateNetwork}:${row.merchantId}`}
                        row={row}
                      />
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </MetricSourceContext.Provider>
      ) : null}
    </>
  );
};

export default AgenticCheckoutPage;
